import { ContainsDropOversWrapper, Portal } from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ensureNotNil, isNil, isNotNil } from "client/Misc/utils";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	SharePointEvent,
	SiteExtendedInfoLoadedEventData,
	subscribe
} from "client_legacy/Documents/SharePoint/documentEvents";
import {
	DocumentsViewContainerViewModel,
	DocumentsViewContainerViewModelExtendedInfo,
	DocumentsViewsStructureModel,
	getDocumentsViewStructure,
	getBreadCrumbsContentByFolder,
	LoadedSiteContentViewModel
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import {
	DocumentsViewModel,
	DocumentsViewType,
	FolderViewModel,
	LoadedExtendedInfo,
	LoadingState,
	Permissions,
	SharePointDocumentsSettingsModel,
} from "client/Api/Documents/SharePoint/common";
import { ExtendedInfoRequester } from "client_legacy/Documents/SharePoint/extendedInfoRequester";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { Folderish, RootFolder } from "client_legacy/Documents/SharePoint/models";
import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { SharePointPageContent } from "./SharePointPageContent";
import { SharePointSideBarHandle, SharePointSideBarMenu } from "./SharePointSideBarMenu";
import {
	createStackItem,
	FolderNavigationStack,
	FolderNavigationStackItem,
	useFolderNavigationStack
} from "./useFolderNavigationStack";
import { IStoreHistoryState } from "client_legacy/Documents/documentStore";
import { SharePointRecycleBinPageContent } from "./RecycleBin/SharePointRecycleBinPageContent";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { useUnableToOpenDocumentLocationDialog } from "../UnableToOpenDocumentLocationDialog";
import { getSharePointLocationUrl } from "../routing";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";
import { SharePointRecentlyModifiedPageContent } from "./RecentlyModified/SharePointRecentlyModifiedPageContent";
import { never } from "client_legacy/ndaw/errors";
import { UploadBatchHandlers } from "../DocumentUploader/useDocumentUploader";

export type ViewsLoadingState = { kind: "loading"; }
	| { kind: "error"; }
	| {
		kind: "success";
		items: readonly DocumentsViewContainerViewModel[];
	};

export type SetLocation = (
	view: DocumentsViewModel,
	folder: Folderish
) => void;

export type SharePointStoreProps = {
	sideBarContainer: HTMLElement;
	hasInitializationError: boolean;
	navigationState: SharePointHistoryState | SharePointActivationContext | undefined;
	onChangeNavigationState(state: SharePointHistoryState): void;
	onSideBarContentIsLoaded(): void;
	onSideBarNavigation(): void;
	onDoesSidebarContainDropOversChange(value: boolean): void;

	settings: SharePointDocumentsSettingsModel;
	maxNumberOfFilesToDownloadAsZip: number;
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
	onDocumentUpload: (
		files: readonly FileSelectionEntry[],
		handler: UploadBatchHandlers
	) => void;
};

export const SharePointStore = (
	props: SharePointStoreProps
): ReactElement => {

	const {
		sideBarContainer,
		hasInitializationError,
		navigationState,
		onChangeNavigationState,
		onSideBarContentIsLoaded,
		onSideBarNavigation,
		onDoesSidebarContainDropOversChange,
		settings,
		maxNumberOfFilesToDownloadAsZip,
		layoutKind,
		onLayoutKindChange,
		onDocumentUpload
	} = props;

	const {
		columnsVisibility: initialColumnConfiguration,
		isSharingViaLinkEnabled,
		syncClient,
		templateAvailabilityKind,
	} = settings;

	const extendedInfoRequester = useMemo(
		(): ExtendedInfoRequester => new ExtendedInfoRequester(),
		[]
	);

	const [currentView, setCurrentView] = useState<DocumentsViewModel | undefined>();
	const [viewsLoadingState, setViewsLoadingState] = useState<ViewsLoadingState>({ kind: "loading" });

	const sideBarRef: RefObject<SharePointSideBarHandle | null> = useRef<SharePointSideBarHandle>(null);
	const stack: FolderNavigationStack = useFolderNavigationStack();
	const setLocationRef: RefObject<SetLocation | null> = useRef<SetLocation>(null);
	const setLocation: SetLocation = useSharePointSetLocation(setCurrentView, stack);
	setLocationRef.current = setLocation;

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const [unableToOpenDocumentLocationDialog, openUnableToOpenDocumentLocationDialog] = useUnableToOpenDocumentLocationDialog();

	const isActive: boolean = isNotNil(navigationState);

	useEffect(
		() => {
			setTimeout(
				() => {
					if (viewsLoadingState.kind === "success"
						&& isActive
						&& currentView
					) {

						if (!sideBarRef.current) {
							throw Error("Sidebar's ref is not set");
						}

						sideBarRef.current
							.expandParentNodes(currentView.id);
					}
				},
				1
			);
		},
		[
			viewsLoadingState.kind,
			isActive,
			currentView
		]
	);

	const sideBarContentIsLoadedHandler = useCallback(
		(
			rootItems: readonly DocumentsViewContainerViewModel[]
		): void => {
			onSideBarContentIsLoaded();

			const siteUrlsToLoadDataFor: string[] = rootItems
				.filter(
					item => item.extendedInfo.state === LoadingState.NotLoaded
				)
				.map(x => x.url);

			extendedInfoRequester.queueFor(siteUrlsToLoadDataFor);
		},
		[extendedInfoRequester, onSideBarContentIsLoaded]
	);

	const loadViews = useCallback(
		async (): Promise<void> => {
			try {
				setViewsLoadingState({ kind: "loading" });
				const result: DocumentsViewsStructureModel = await getDocumentsViewStructure(false, globalCancellation);

				setViewsLoadingState({
					kind: "success",
					items: result.structure
				});
				sideBarContentIsLoadedHandler(result.structure);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setViewsLoadingState({ kind: "error" });
			}
		},
		[
			alertManager,
			globalCancellation,
			sideBarContentIsLoadedHandler
		]
	);

	useEffect(
		() => {
			if (hasInitializationError) {
				setViewsLoadingState({ kind: "error" });
			} else {
				loadViews();
			}
		},
		[
			alertManager,
			hasInitializationError,
			loadViews
		]
	);

	usePubSubEvents(
		setCurrentView,
		setViewsLoadingState
	);

	const selectViewHandler = useCallback(
		(view: DocumentsViewModel): void => {
			onSideBarNavigation();

			const currentFolderId: number | nil = navigationState?.folder
				?.id;

			const currentViewId: string | undefined = navigationState?.view
				.id;

			if (isNil(currentFolderId) && currentViewId === view.id) {
				return;
			}

			onChangeNavigationState({
				store: DocumentStoreType.SharePoint,
				view,
				folder: createRootFolderFromView(view)
			});
		},
		[
			navigationState?.folder?.id,
			navigationState?.view.id,
			onChangeNavigationState,
			onSideBarNavigation
		]
	);

	const openFolderHandler = useCallback(
		(folder: Folderish): void => {
			onChangeNavigationState({
				store: DocumentStoreType.SharePoint,
				view: ensureNotNil(currentView),
				folder
			});
		},
		[
			currentView,
			onChangeNavigationState
		]
	);

	const { set: setNavigationStack } = stack;

	const folderPermissionsLoadedHandler = useCallback(
		(viewId: string, folderId: number | null, permissions: Permissions): void => {
			setNavigationStack(
				current => {
					const index: number = current.findIndex(
						x => x.viewId === viewId &&
							x.id === folderId && !x.folder.permissions
					);

					if (index === -1) {
						return current;
					}

					const copy: FolderNavigationStackItem[] = [...current];

					const item: FolderNavigationStackItem = copy[index];

					copy[index] = {
						...item,
						folder: {
							...item.folder,
							permissions
						}
					};

					return copy;
				}
			);
		},
		[
			setNavigationStack
		]
	);

	useEffect(
		(): void => {
			if (!navigationState) {
				setCurrentView(undefined);
				return;
			}

			if (hasDidRequestSpecificLocationButCouldNotServe(navigationState)
				&& navigationState.didRequestSpecificLocationButCouldNotServe
			) {
				openUnableToOpenDocumentLocationDialog()
					.then(
						() => {
							onChangeNavigationState({
								store: DocumentStoreType.SharePoint,
								view: navigationState.view,
								folder: isNil(navigationState.folder)
									? createRootFolderFromView(navigationState.view)
									: !isFolderIsh(navigationState.folder)
										? { ...navigationState.folder, isRoot: false }
										: navigationState.folder
							});
						}
					);

				return;
			}

			if (!navigationState.folder) {
				onChangeNavigationState({
					store: DocumentStoreType.SharePoint,
					view: navigationState.view,
					folder: createRootFolderFromView(navigationState.view)
				});
				return;
			}

			const folder: Folderish = !isFolderIsh(navigationState.folder)
				? { ...navigationState.folder, isRoot: false }
				: navigationState.folder;

			// We use a ref to the latest "setLocation" callback, because the "setLocation" has a dependency on the navigation stack
			// and it mutates the navigation stack. If we were to do it proper, it would mean that this useEffect fires multiple
			// times due to the "setLocation" function changes, while we intend to only have it "fire" when the navigationState (prop)
			// changes.
			setLocationRef.current?.(navigationState.view, folder);
		},
		[
			navigationState,
			onChangeNavigationState,
			openUnableToOpenDocumentLocationDialog
		]
	);

	const siteContentLoadedHandler = useCallback(
		(id: string, content: LoadedSiteContentViewModel): void => {
			setViewsLoadingState(
				(x) => applySiteContent(
					x,
					id,
					content
				)
			);
		},
		[]
	);

	const renderPageContent = (): ReactElement | null => {
		if (!isActive || !currentView) {
			return null;
		}

		switch(currentView.type) {
			case DocumentsViewType.RecycleBinView:
				return (
					<SharePointRecycleBinPageContent
						isMobile={isMobile}
						view={currentView}
						folderPath={stack.items}
						layoutKind={layoutKind}
						onLayoutKindChange={onLayoutKindChange}
					/>
				);
			case DocumentsViewType.RecentlyModified:
				return (
					<SharePointRecentlyModifiedPageContent
						isMobile={isMobile}
						view={currentView}
						folderPath={stack.items}
						layoutKind={layoutKind}
						onLayoutKindChange={onLayoutKindChange}
						isSharingViaLinkEnabled={isSharingViaLinkEnabled}
					/>
				);
			case DocumentsViewType.EditableView:
			case DocumentsViewType.SharedWithMeView:
				return (
					<SharePointPageContent
						isMobile={isMobile}
						isResponsive={isResponsive}
						view={currentView}
						folderPath={stack.items}
						onOpenFolder={openFolderHandler}
						onFolderPermissionsLoaded={folderPermissionsLoadedHandler}
						initialColumnConfiguration={initialColumnConfiguration}
						isSharingViaLinkEnabled={isSharingViaLinkEnabled}
						maxNumberOfFilesToDownloadAsZip={maxNumberOfFilesToDownloadAsZip}
						syncClient={syncClient}
						templateAvailabilityKind={templateAvailabilityKind}
						layoutKind={layoutKind}
						onLayoutKindChange={onLayoutKindChange}
						onDocumentUpload={onDocumentUpload}
					/>
				);
			default:
				never(currentView.type);
		}
	};

	return (
		<>
			<Portal container={sideBarContainer}>
				<ContainsDropOversWrapper onDoesContainDropOversChange={onDoesSidebarContainDropOversChange}>
					<SharePointSideBarMenu
						ref={sideBarRef}
						viewsLoadingState={viewsLoadingState}
						onSelectView={selectViewHandler}
						extendedInfoRequester={extendedInfoRequester}
						selectedView={currentView}
						onActionInvoked={onSideBarNavigation}
						onSiteContentLoaded={siteContentLoadedHandler}
					/>
				</ContainsDropOversWrapper>
			</Portal>
			{renderPageContent()}
			{unableToOpenDocumentLocationDialog}
		</>
	);
};
SharePointStore.displayName = "SharePointStore";

export function useSharePointSetLocation(
	setCurrentView: (view: DocumentsViewModel) => void,
	stack: FolderNavigationStack
): SetLocation {
	const stackLoadingCancellation: RefObject<AbortController | undefined> = useRef<AbortController>(undefined);

	return useCallback(
		(
			view: DocumentsViewModel,
			folder: Folderish | undefined
		): void => {
			stackLoadingCancellation.current
				?.abort();

			setCurrentView(view);

			if (!folder) {
				folder = createRootFolderFromView(view);
			}

			const newStackItem: FolderNavigationStackItem = createStackItem(folder);

			if (folder.isRoot) {
				stack.set([newStackItem]);
				return;
			}

			const folderId: number = folder.id;

			const isFolderPresentInExistingStack: boolean = stack.items
				.findIndex(x => x.id === folderId) !== -1;

			if (isFolderPresentInExistingStack) {
				stack.navigateBackTo(folder.id);
				return;
			}

			if (stack.items.length > 0) {
				const lastFolder: FolderNavigationStackItem = stack.items[stack.items.length - 1];
				const isLastFolderTheParentOfTheNewFolder: boolean = folder.viewId === lastFolder.viewId
					&& folder.parentFolderId === lastFolder.id;

				if (isLastFolderTheParentOfTheNewFolder) {
					stack.navigateForwardTo(newStackItem);
					return;
				}
			}

			// in shared with me, we don't know where the arbitrary folder might be located, so attach to the shared with me root
			if (view.type === DocumentsViewType.SharedWithMeView) {
				const rootFolder: RootFolder = createRootFolderFromView(view);
				stack.set([
					createStackItem(rootFolder),
					newStackItem
				]);
				return;
			}

			const cancellation = new AbortController();
			stackLoadingCancellation.current = cancellation;

			const rootFolder: RootFolder = createRootFolderFromView(view);

			// While loading the breadcrumbs from the backend (which could some time), we already
			// Show a abbreviated version of the stack so the rest of the folder content can load already
			stack.set([
				createStackItem(rootFolder),
				newStackItem
			]);

			getBreadCrumbsContentByFolder(
				folder.id,
				convertToLocationModel(folder.location),
				cancellation.signal
			)
				.then(
					(folders: FolderViewModel[]) => {
						const stackItemsInBetween: readonly FolderNavigationStackItem[] = folders.map(x => createStackItem({ ...x, isRoot: false }));
						stack.set([
							createStackItem(rootFolder),
							...stackItemsInBetween,
							newStackItem
						]);
					}
				)
				.catch((e) => {
					if (isCancelledError(e)) {
						return;
					}
					console.error("Failed to load the folder stack from the backend", e);
					stack.reset();
				});
		},
		[setCurrentView, stack]
	);

}

export function createRootFolderFromView(view: DocumentsViewModel): RootFolder {
	return {
		id: null,
		location: view.location,
		viewId: view.id,
		name: view.title,
		isRoot: true,
	};
}

function usePubSubEvents(
	setCurrentView: Dispatch<SetStateAction<DocumentsViewModel | undefined>>,
	setViewsLoadingState: Dispatch<SetStateAction<ViewsLoadingState>>
): void {
	useEffect(
		(): (() => void) => {
			const tokens: SubscriptionTokenCollection = registerPubSubEvents(
				setViewsLoadingState
			);

			return () => tokens.destroy();
		},
		[
			setCurrentView,
			setViewsLoadingState
		]
	);
}

function registerPubSubEvents(
	setViewsLoadingState: Dispatch<SetStateAction<ViewsLoadingState>>
): SubscriptionTokenCollection {
	const result = new SubscriptionTokenCollection();

	const siteExtendedInfoLoadedToken: PubSubSubscriptionToken = subscribe(
		SharePointEvent.SiteExtendedInfoLoaded,
		(_, { containerId, extendedInfo }: SiteExtendedInfoLoadedEventData) => {
			setViewsLoadingState( state => {
				if (state.kind !== "success") {
					return state;
				}

				return {
					...state,
					items: setContainerSiteExtendedInfo(
						containerId,
						extendedInfo,
						state.items
					)
				};
			});
		}
	);

	result.add(siteExtendedInfoLoadedToken);

	return result;
}

export interface SharePointActivationContext {
	view: DocumentsViewModel;
	folder: FolderViewModel | null;
	didRequestSpecificLocationButCouldNotServe?: boolean;
}

export interface SharePointHistoryState extends IStoreHistoryState {
	store: DocumentStoreType.SharePoint;
	folder: Folderish;
	view: DocumentsViewModel;
}

export function createSharePointUrlFromState(state: IStoreHistoryState): string {
	if (state.store !== DocumentStoreType.SharePoint) {
		throw new Error("State is not for SharePoint");
	}

	if (!hasView(state)) {
		throw new Error("State has no view specified");
	}

	const view: DocumentsViewModel = state.view;

	if (hasFolder(state)) {
		const { folder } = state;
		return getSharePointLocationUrl(folder.location, folder.id);
	} else {
		return getSharePointLocationUrl(view.location);
	}
}

export function buildSharePointActivationContext(
	view: DocumentsViewModel,
	folder: FolderViewModel | null
): SharePointActivationContext {
	return {
		view,
		folder
	};
}

function hasView(value: unknown): value is { view: NonNullable<SharePointHistoryState["view"] | SharePointActivationContext["view"]>; } {
	return isNotNil(
		(value as SharePointHistoryState | SharePointActivationContext).view
	);
}

function hasFolder(value: unknown): value is { folder: NonNullable<SharePointHistoryState["folder"] | SharePointActivationContext["folder"]>; } {
	return isNotNil(
		(value as SharePointHistoryState | SharePointActivationContext).folder
	);
}

function hasDidRequestSpecificLocationButCouldNotServe(value: unknown): value is { didRequestSpecificLocationButCouldNotServe: NonNullable<SharePointActivationContext["didRequestSpecificLocationButCouldNotServe"]>; } {
	return isNotNil(
		(value as SharePointActivationContext).didRequestSpecificLocationButCouldNotServe
	);
};

function isFolderIsh(value: unknown): value is Folderish {
	return isNotNil(
		(value as Folderish).isRoot
	);
}

function setContainerSiteExtendedInfo(
	id: string,
	extendedInfo: LoadedExtendedInfo<DocumentsViewContainerViewModelExtendedInfo>,
	items: readonly DocumentsViewContainerViewModel[]
): DocumentsViewContainerViewModel[] {
	return items.map(
		(item): DocumentsViewContainerViewModel => {
			const { content, ...restOfItem } = item;
			return ({
				...restOfItem,
				extendedInfo: id === item.id
					? extendedInfo
					: item.extendedInfo,
				content: content.isLoaded
					? {
						...content,
						subContainers: setContainerSiteExtendedInfo(
							id,
							extendedInfo,
							content.subContainers
						)
					}
					: item.content

			});
		}
	);
}

export function applySiteContent(
	state: ViewsLoadingState,
	id: string,
	content: LoadedSiteContentViewModel
): ViewsLoadingState {
	const applySiteContent = (item: DocumentsViewContainerViewModel): DocumentsViewContainerViewModel => {
		if (item.id === id) {
			return {
				...item,
				content
			};
		}

		if (!item.content.isLoaded) {
			return item;
		}

		const subContainers: DocumentsViewContainerViewModel[] = [];
		let hasMutation: boolean = false;

		for (const subContainer of item.content.subContainers) {
			const replacementSubContainer: DocumentsViewContainerViewModel = applySiteContent(subContainer);
			hasMutation ||= !Object.is(replacementSubContainer, subContainer);
			subContainers.push(replacementSubContainer);
		}

		if (!hasMutation) {
			return item;
		}

		return {
			...item,
			content: {
				...item.content,
				subContainers
			}
		};
	};

	if (state.kind !== "success") {
		return state;
	}

	return {
		...state,
		items: state.items
			.map(applySiteContent)
	};
}
